import React, { useState } from 'react'
import { deformTypes } from '../utils/shapeEngine'

/**
 * ShapeEffectsPanel
 * -----------------
 * Shape-specific geometry controls (Phases 2-4): polygon/star params, corner
 * radius, vector deformations, border/stroke mechanics (dash, marching ants,
 * draw-on / trim, multiple outlines), the shape repeater, and shape morph.
 *
 * Writes item.sides / item.innerRadius / item.cornerRadius / item.shapeDeform /
 * item.shapeBorder / item.shapeRepeat / item.shapeMorph. Read by shapeEngine.js
 * (preview) and shape_engine.py (worker).
 */

const DEFORM_LABELS = {
  taper: 'Taper', twist: 'Twist', bend: 'Bend', inflate: 'Inflate / Deflate',
  wave: 'Wave', pinch: 'Pinch / Bulge', ripple: 'Ripple',
}

const MORPH_TARGETS = ['rectangle', 'circle', 'ellipse', 'triangle', 'diamond', 'pentagon', 'hexagon', 'star', 'arrow']

const Section = ({ title, children, right }) => (
  <div className="bg-gray-50 p-3 rounded-lg">
    <div className="flex items-center justify-between mb-2">
      <h4 className="text-sm font-medium text-gray-700">{title}</h4>
      {right}
    </div>
    {children}
  </div>
)

const Num = ({ label, value, fallback, min, max, step, onChange }) => (
  <div>
    <label className="text-[11px] text-gray-500 block mb-0.5">{label}</label>
    <input type="number" value={value ?? fallback}
      onChange={(e) => onChange(parseFloat(e.target.value))}
      className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
      min={min} max={max} step={step} />
  </div>
)

const Check = ({ label, value, onChange }) => (
  <label className="flex items-center gap-2 text-xs text-gray-600">
    <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)}
      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
    {label}
  </label>
)

export default function ShapeEffectsPanel({ element, selectedElement, updateElementProperty }) {
  if (!element || element.type !== 'shape') return null
  const set = (key, value) => updateElementProperty(selectedElement, key, value)
  const setObj = (key, patch) => updateElementProperty(selectedElement, key, { ...(element[key] || {}), ...patch })

  const st = element.shapeType || 'rectangle'
  const deform = element.shapeDeform || {}
  const border = element.shapeBorder || {}
  const repeat = element.shapeRepeat || {}
  const morph = element.shapeMorph || {}

  return (
    <div className="space-y-3">
      {/* Polygon / Star params */}
      {(st === 'polygon' || st === 'star') && (
        <Section title="Points">
          <div className="grid grid-cols-2 gap-2">
            <Num label="Sides / Points" value={element.sides} fallback={st === 'star' ? 5 : 6} min={3} max={20} step={1}
              onChange={(v) => set('sides', Math.max(3, Math.round(v || 3)))} />
            {st === 'star' && (
              <Num label="Inner Radius" value={element.innerRadius} fallback={0.4} min={0.05} max={1} step={0.05}
                onChange={(v) => set('innerRadius', Math.max(0.05, Math.min(1, v)))} />
            )}
          </div>
        </Section>
      )}

      {/* Corner radius (rect) */}
      {(st === 'rectangle' || st === 'square') && (
        <Section title="Corner Radius">
          <Num label="Radius (px)" value={element.cornerRadius ?? element.borderRadius} fallback={0} min={0} step={1}
            onChange={(v) => set('cornerRadius', Math.max(0, v || 0))} />
        </Section>
      )}

      {/* Deformation */}
      <Section title="Deformation" right={
        deform.type ? <button onClick={() => set('shapeDeform', undefined)} className="text-xs text-red-500 hover:text-red-700">Clear</button> : null
      }>
        <div className="space-y-2">
          <select value={deform.type || ''}
            onChange={(e) => set('shapeDeform', e.target.value ? { ...deform, type: e.target.value, amount: deform.amount ?? 0.4 } : undefined)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">None</option>
            {deformTypes().map((t) => <option key={t} value={t}>{DEFORM_LABELS[t] || t}</option>)}
          </select>
          {deform.type && (
            <>
              <Num label="Amount" value={deform.amount} fallback={0.4} min={-1} max={2} step={0.05}
                onChange={(v) => setObj('shapeDeform', { amount: v })} />
              {(deform.type === 'wave' || deform.type === 'ripple') && (
                <div className="grid grid-cols-2 gap-2">
                  <Num label="Frequency" value={deform.frequency} fallback={deform.type === 'ripple' ? 5 : 3} min={1} step={1}
                    onChange={(v) => setObj('shapeDeform', { frequency: v })} />
                  <Num label="Speed" value={deform.speed} fallback={0.5} min={0} step={0.1}
                    onChange={(v) => setObj('shapeDeform', { speed: v })} />
                </div>
              )}
              {deform.type === 'wave' && (
                <select value={deform.axis || 'x'} onChange={(e) => setObj('shapeDeform', { axis: e.target.value })}
                  className="w-full border border-gray-300 rounded px-2 py-1 text-xs">
                  <option value="x">Along X</option>
                  <option value="y">Along Y</option>
                </select>
              )}
              <Check label="Animate" value={deform.animate} onChange={(v) => setObj('shapeDeform', { animate: v })} />
            </>
          )}
        </div>
      </Section>

      {/* Border mechanics */}
      <Section title="Border & Stroke" right={
        element.shapeBorder ? <button onClick={() => set('shapeBorder', undefined)} className="text-xs text-red-500 hover:text-red-700">Clear</button> : null
      }>
        <div className="space-y-2">
          <Check label="Dashed border" value={Array.isArray(border.dash)} onChange={(v) => setObj('shapeBorder', { dash: v ? [8, 6] : null })} />
          {Array.isArray(border.dash) && (
            <div className="grid grid-cols-2 gap-2">
              <Num label="Dash" value={border.dash[0]} fallback={8} min={1} step={1}
                onChange={(v) => setObj('shapeBorder', { dash: [Math.max(1, v || 1), border.dash[1] ?? 6] })} />
              <Num label="Gap" value={border.dash[1]} fallback={6} min={1} step={1}
                onChange={(v) => setObj('shapeBorder', { dash: [border.dash[0] ?? 8, Math.max(1, v || 1)] })} />
            </div>
          )}
          <Check label="Marching ants (animate dashes)" value={border.marchingAnts} onChange={(v) => setObj('shapeBorder', { marchingAnts: v })} />
          {border.marchingAnts && (
            <Num label="March Speed" value={border.marchSpeed} fallback={30} min={1} step={5}
              onChange={(v) => setObj('shapeBorder', { marchSpeed: v })} />
          )}
          <Check label="Draw-on (animate outline)" value={border.drawOn} onChange={(v) => setObj('shapeBorder', { drawOn: v })} />
          {border.drawOn && (
            <Num label="Draw Speed" value={border.drawSpeed} fallback={0.5} min={0.05} step={0.05}
              onChange={(v) => setObj('shapeBorder', { drawSpeed: v })} />
          )}
          {!border.drawOn && (
            <div className="grid grid-cols-2 gap-2">
              <Num label="Trim Start" value={border.trimStart} fallback={0} min={0} max={1} step={0.05}
                onChange={(v) => setObj('shapeBorder', { trimStart: v })} />
              <Num label="Trim End" value={border.trimEnd} fallback={1} min={0} max={1} step={0.05}
                onChange={(v) => setObj('shapeBorder', { trimEnd: v })} />
            </div>
          )}
        </div>
      </Section>

      {/* Repeater */}
      <Section title="Repeater" right={
        repeat.mode ? <button onClick={() => set('shapeRepeat', undefined)} className="text-xs text-red-500 hover:text-red-700">Clear</button> : null
      }>
        <div className="space-y-2">
          <select value={repeat.mode || ''}
            onChange={(e) => set('shapeRepeat', e.target.value ? { ...repeat, mode: e.target.value, count: repeat.count || 4 } : undefined)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">None</option>
            <option value="grid">Grid</option>
            <option value="line">Line</option>
            <option value="circle">Circle</option>
          </select>
          {repeat.mode && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <Num label="Count" value={repeat.count} fallback={4} min={1} max={100} step={1}
                  onChange={(v) => setObj('shapeRepeat', { count: Math.max(1, Math.round(v || 1)) })} />
                {repeat.mode === 'grid' && (
                  <Num label="Columns" value={repeat.cols} fallback={3} min={1} step={1}
                    onChange={(v) => setObj('shapeRepeat', { cols: Math.max(1, Math.round(v || 1)) })} />
                )}
                {repeat.mode === 'circle' && (
                  <Num label="Radius" value={repeat.radius} fallback={120} min={0} step={10}
                    onChange={(v) => setObj('shapeRepeat', { radius: v })} />
                )}
              </div>
              {(repeat.mode === 'grid' || repeat.mode === 'line') && (
                <div className="grid grid-cols-2 gap-2">
                  <Num label="Spacing X" value={repeat.spacingX} fallback={(element.width || 40) + 10} step={5}
                    onChange={(v) => setObj('shapeRepeat', { spacingX: v })} />
                  <Num label="Spacing Y" value={repeat.spacingY} fallback={repeat.mode === 'grid' ? (element.height || 40) + 10 : 0} step={5}
                    onChange={(v) => setObj('shapeRepeat', { spacingY: v })} />
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <Num label="Rotate Each °" value={repeat.rotateEach} fallback={0} step={5}
                  onChange={(v) => setObj('shapeRepeat', { rotateEach: v })} />
                <Num label="Scale Each" value={repeat.scaleEach} fallback={1} min={0.1} step={0.05}
                  onChange={(v) => setObj('shapeRepeat', { scaleEach: v })} />
              </div>
            </>
          )}
        </div>
      </Section>

      {/* Morph */}
      <Section title="Shape Morph" right={
        morph.target ? <button onClick={() => set('shapeMorph', undefined)} className="text-xs text-red-500 hover:text-red-700">Clear</button> : null
      }>
        <div className="space-y-2">
          <select value={morph.target || ''}
            onChange={(e) => set('shapeMorph', e.target.value ? { ...morph, target: e.target.value, progress: morph.progress ?? 0.5 } : undefined)}
            className="w-full border border-gray-300 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">None</option>
            {MORPH_TARGETS.filter((t) => t !== st).map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          {morph.target && (
            <>
              <Num label="Progress" value={morph.progress} fallback={0.5} min={0} max={1} step={0.05}
                onChange={(v) => setObj('shapeMorph', { progress: v })} />
              <Check label="Animate" value={morph.animate} onChange={(v) => setObj('shapeMorph', { animate: v })} />
              {morph.animate && (
                <Num label="Duration (s)" value={morph.duration} fallback={1} min={0.1} step={0.1}
                  onChange={(v) => setObj('shapeMorph', { duration: v })} />
              )}
            </>
          )}
        </div>
      </Section>
    </div>
  )
}
